import { Button, Cell, Grid, HFlow, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacao } from 'components/auth/useAcessoLotacao'
import { useErrorHandler } from 'components/error'
import {
  CidadaoAtendimentoSelectField,
  CidadaoAtendimentoSelectFieldModel,
  EquipeSelectField,
  Form,
  FormRenderProps,
  SubmitButton,
} from 'components/form'
import { FormApi } from 'final-form'
import createDecorator from 'final-form-calculate'
import { useApolloClient } from 'graphql/hooks'
import {
  useAgendamentosDiaLazyQuery,
  useCidadaoLazyQuery,
  useSalvarAtendimentoAgendadoMutation,
  useSalvarAtendimentoMutation,
} from 'graphql/hooks.generated'
import { SituacaoAgendado } from 'graphql/types.generated'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'
import { metaPath } from 'util/metaPath'
import { createValidator, required } from 'util/validation'
import { TipoServicoCheckField } from 'view/configuracoes/municipal/tipo-servico/TipoServicoCheckField'
import { LotacaoSelectField } from 'view/profissional/lotacao/LotacaoSelectField'

import { AgendamentosDiaField } from '../agenda/AgendamentosDiaField'
import { createNovoAtendimentoCalculations } from '../calculator'
import { convertNovoAtendimentoModelToInput } from '../converter'
import { NovoAtendimentoFormModel } from '../model'
import { resolveEquipeTooltip, resolveProfissionalTooltip, resolveTiposServicoTooltip } from '../tooltipResolver'

export interface ListaAtendimentoFormModel extends NovoAtendimentoFormModel {
  cidadao?: CidadaoAtendimentoSelectFieldModel
}

const path = metaPath<ListaAtendimentoFormModel>()

const validate = createValidator<ListaAtendimentoFormModel>({
  cidadao: required,
})

export interface ListaAtendimentoFormProps {
  cidadaoId?: string
}

const cidadaoCalculator = {
  field: path.cidadao.absolutePath(),
  updates: {
    [path.agendados.absolutePath()]: (value, allValues: ListaAtendimentoFormModel) => {
      if (allValues.agendados) {
        return undefined
      }
    },
  },
}

export function ListaAtendimentoForm(props: ListaAtendimentoFormProps) {
  const { cidadaoId } = props

  const apollo = useApolloClient()
  const alert = useAlert()
  const history = useHistory()
  const { unidadeSaude } = useAcessoLotacao()
  const handleRejection = useErrorHandler()
  const [salvarAtendimentoMutation] = useSalvarAtendimentoMutation()
  const [salvarAtendimentoAgendadoMutation] = useSalvarAtendimentoAgendadoMutation()
  const [executeCidadaoQuery, { data: cidadaoData }] = useCidadaoLazyQuery({ variables: { id: cidadaoId } })
  const [executeAgendamentosDiaQuery, { data: agendamentosDiaData }] = useAgendamentosDiaLazyQuery()
  const executeAgendamentosDiaCidadaoQuery = useCallback(
    (cidadaoId: ID) =>
      executeAgendamentosDiaQuery({
        variables: {
          input: {
            cidadao: cidadaoId,
            dataAgendado: moment().startOf('day').toDate(),
            situacao: [SituacaoAgendado.AGENDADO],
            unidadeSaude: unidadeSaude.id,
            foraDaUbs: false,
            origemAtencaoDomiciliar: false,
          },
        },
      }),
    [executeAgendamentosDiaQuery, unidadeSaude.id]
  )

  useEffect(() => {
    if (cidadaoId) {
      executeCidadaoQuery({ variables: { id: cidadaoId } })
      executeAgendamentosDiaCidadaoQuery(cidadaoId)
    }
  }, [cidadaoId, executeCidadaoQuery, executeAgendamentosDiaCidadaoQuery])

  const initialValues: ListaAtendimentoFormModel = useMemo(
    () => ({
      cidadao: cidadaoId && cidadaoData && { ...cidadaoData.cidadao, presenteListaAtendimento: false },
    }),
    [cidadaoData, cidadaoId]
  )

  const handleCidadaoChange = (newValue: CidadaoAtendimentoSelectFieldModel) => {
    if (newValue && newValue.id) executeAgendamentosDiaCidadaoQuery(newValue.id)
  }

  const handleSubmit = (value: ListaAtendimentoFormModel, formApi: FormApi) => {
    if (value?.agendados) {
      return saveAgendado(value, formApi)
    } else {
      return save(value, formApi)
    }
  }

  const save = (value: ListaAtendimentoFormModel, formApi: FormApi) =>
    salvarAtendimentoMutation({
      variables: {
        input: convertNovoAtendimentoModelToInput(value.cidadao.id, value),
      },
    })
      .then((res) => onSubmitSuccess(res, formApi))
      .catch(handleRejection)

  const saveAgendado = (value: ListaAtendimentoFormModel, formApi: FormApi) =>
    salvarAtendimentoAgendadoMutation({
      variables: {
        input: {
          agendado: value?.agendados?.[0],
        },
      },
    })
      .then((res) => onSubmitSuccess(res, formApi))
      .catch(handleRejection)

  const onSubmitSuccess = (result, formApi: FormApi) => {
    alert('success', `Cidadão foi adicionado com sucesso.`)
    formApi.getRegisteredFields().forEach((field) => formApi.resetFieldState(field))
    setTimeout(formApi.reset)
    history.push('/lista-atendimento')
    return result
  }

  const renderForm = (formProps: FormRenderProps<ListaAtendimentoFormModel>) => {
    const values = formProps.values

    return (
      <Grid>
        <Cell size={5}>
          <CidadaoAtendimentoSelectField
            name={path.cidadao}
            ativo={true}
            obito={false}
            label='Cidadão'
            onChange={handleCidadaoChange}
            required
          />
        </Cell>
        <Cell size={7} />

        <AgendamentosDiaField
          name={path.agendados}
          agendamentosDia={values?.cidadao && agendamentosDiaData?.agendados?.content}
        />

        <Cell size={5}>
          <Grid>
            <Cell size={12}>
              <Tooltip text={resolveProfissionalTooltip(values)}>
                <LotacaoSelectField
                  name={path.lotacao}
                  label='Profissional'
                  unidadeSaudeId={unidadeSaude.id}
                  disabled={(values?.equipe && !values?.lotacao) || !!values?.agendados?.length}
                />
              </Tooltip>
            </Cell>
            <Cell size={12}>
              <Tooltip text={resolveEquipeTooltip(values)}>
                <EquipeSelectField
                  name={path.equipe}
                  label='Equipe'
                  unidadeSaudeId={unidadeSaude.id}
                  disabled={!!values?.lotacao || !!values?.agendados?.length}
                />
              </Tooltip>
            </Cell>
          </Grid>
        </Cell>
        <Cell size={7}>
          <Tooltip text={resolveTiposServicoTooltip(values)}>
            <div>
              <TipoServicoCheckField name={path.tiposServico} disabled={!!values?.agendados?.length} />
            </div>
          </Tooltip>
        </Cell>
        <Cell alignSelf='flex-end' size={12}>
          <HFlow justifyContent='flex-end'>
            <Button size='medium' onClick={formProps.form.reset}>
              Limpar campos
            </Button>
            <SubmitButton size='medium' handleSubmit={formProps.handleSubmit}>
              Adicionar
            </SubmitButton>
          </HFlow>
        </Cell>
      </Grid>
    )
  }

  const decorator = useMemo(() => createDecorator(...createNovoAtendimentoCalculations(apollo), cidadaoCalculator), [
    apollo,
  ])
  return (
    <Form<ListaAtendimentoFormModel>
      onSubmit={handleSubmit}
      render={renderForm}
      decorators={[decorator]}
      validate={validate}
      initialValues={initialValues}
    />
  )
}
